import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, Loader2 } from "lucide-react";
import api from "../lib/api";
import { fmtPrice, fmtCap, fmtNum, fmtPct, changeClass, trendBadgeClass } from "../lib/format";
import { PriceChart } from "../components/PriceChart";
import { AIResearchPanel } from "../components/AIResearchPanel";
import { AddToWatchlist } from "../components/AddToWatchlist";

const Metric = ({ label, value, testId }) => (
  <div className="p-4 border border-border rounded-sm bg-card" data-testid={testId}>
    <div className="overline-label mb-1.5">{label}</div>
    <div className="font-mono text-base text-white">{value}</div>
  </div>
);

const pctFmt = (v, d = 1) => (v == null ? "—" : `${(v * 100).toFixed(d)}%`);

export default function StockDetail() {
  const { ticker } = useParams();
  const [stock, setStock] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setStock(null);
    setError(null);
    api
      .get(`/stocks/${ticker}`)
      .then((r) => setStock(r.data))
      .catch(() => setError(`${ticker.toUpperCase()} is not in the research universe.`));
  }, [ticker]);

  if (error)
    return (
      <div className="space-y-4">
        <Link to="/scanner" className="text-sm text-primary hover:underline flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back to scanner
        </Link>
        <p className="text-muted-foreground" data-testid="stock-not-found">{error}</p>
      </div>
    );

  if (!stock)
    return (
      <div className="h-64 flex items-center justify-center text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading {ticker.toUpperCase()}…
      </div>
    );

  const f = stock.fundamentals || {};
  const t = stock.technicals || {};
  const q = stock.quote || {};
  const pc = stock.peer_comparison || {};
  const cur = stock.currency;
  const rsi = t.rsi14;
  const rsiColor = rsi == null ? "#71717a" : rsi < 30 ? "#10b981" : rsi > 70 ? "#ef4444" : "#2563eb";

  return (
    <div className="space-y-6 fade-up" data-testid="stock-detail-page">
      {/* Header */}
      <div>
        <Link to="/scanner" className="text-xs font-mono text-muted-foreground hover:text-primary flex items-center gap-1 mb-4 w-fit"
          data-testid="back-to-scanner-link">
          <ArrowLeft className="w-3.5 h-3.5" /> SCANNER
        </Link>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl text-white" data-testid="stock-ticker-heading">
                {stock.ticker}
              </h1>
              <span className="px-2 py-0.5 text-[11px] font-mono uppercase border border-border rounded-sm text-muted-foreground">
                {stock.sector}
              </span>
              <span className="px-2 py-0.5 text-[11px] font-mono uppercase border border-border rounded-sm text-muted-foreground">
                {stock.country}
              </span>
            </div>
            <p className="text-muted-foreground mt-1">{stock.name} · {stock.industry}</p>
          </div>
          <div className="flex items-center gap-5">
            <AddToWatchlist ticker={stock.ticker} />
            <div className="text-right">
              <div className="font-mono text-3xl font-medium text-white" data-testid="stock-price">
                {fmtPrice(q.price, cur)}
              </div>
              <div className={`font-mono text-sm ${changeClass(q.change_pct)}`} data-testid="stock-change">
                {fmtPct(q.change_pct)} today
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Chart + technicals */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <PriceChart ticker={stock.ticker} />
        </div>
        <div className="widget p-4" data-testid="technicals-panel">
          <div className="overline-label mb-4">Technical indicators</div>
          <div className="space-y-3.5 text-sm">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Trend</span>
              <span className={`px-2 py-0.5 text-[11px] font-mono uppercase border rounded-sm ${trendBadgeClass(t.trend)}`} data-testid="technical-trend">
                {t.trend}
              </span>
            </div>
            <div>
              <div className="flex justify-between mb-1.5">
                <span className="text-muted-foreground">RSI (14)</span>
                <span className="font-mono text-white">{fmtNum(rsi, 1)}</span>
              </div>
              <div className="h-1.5 bg-secondary rounded-sm overflow-hidden">
                <div className="h-full" style={{ width: `${Math.min(rsi || 0, 100)}%`, background: rsiColor }} />
              </div>
              <div className="flex justify-between text-[10px] font-mono text-muted-foreground/60 mt-1">
                <span>30 OVERSOLD</span><span>70 OVERBOUGHT</span>
              </div>
            </div>
            {[
              ["SMA 50", fmtPrice(t.sma50, cur)],
              ["SMA 200", fmtPrice(t.sma200, cur)],
              ["MACD", `${fmtNum(t.macd)} / sig ${fmtNum(t.macd_signal)}`],
              ["52W high", `${fmtPrice(t.high_52w, cur)} (${fmtPct(t.pct_from_52w_high, 1)})`],
              ["52W low", `${fmtPrice(t.low_52w, cur)} (${fmtPct(t.pct_from_52w_low, 1)})`],
            ].map(([label, value]) => (
              <div key={label} className="flex justify-between border-t border-border/60 pt-3">
                <span className="text-muted-foreground">{label}</span>
                <span className="font-mono text-white text-right">{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Fundamentals */}
      <div data-testid="fundamentals-grid">
        <div className="overline-label mb-3">Key fundamentals</div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <Metric label="Market cap" value={fmtCap(f.market_cap, cur)} testId="metric-market-cap" />
          <Metric label="PE (ttm)" value={fmtNum(f.pe, 1)} testId="metric-pe" />
          <Metric label="Forward PE" value={fmtNum(f.forward_pe, 1)} testId="metric-forward-pe" />
          <Metric label="PEG" value={fmtNum(f.peg)} testId="metric-peg" />
          <Metric label="EPS (ttm)" value={fmtNum(f.eps)} testId="metric-eps" />
          <Metric label="Beta" value={fmtNum(f.beta)} testId="metric-beta" />
          <Metric label="Profit margin" value={pctFmt(f.profit_margin)} testId="metric-profit-margin" />
          <Metric label="Op. margin" value={pctFmt(f.operating_margin)} testId="metric-op-margin" />
          <Metric label="ROE" value={pctFmt(f.roe)} testId="metric-roe" />
          <Metric label="Debt / equity" value={f.debt_to_equity != null ? fmtNum(f.debt_to_equity / 100) : "—"} testId="metric-de" />
          <Metric label="Revenue growth" value={pctFmt(f.revenue_growth)} testId="metric-rev-growth" />
          <Metric label="Dividend yield" value={f.dividend_yield != null ? `${fmtNum(f.dividend_yield)}%` : "—"} testId="metric-div-yield" />
        </div>
      </div>

      {/* Peer comparison + news */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="widget" data-testid="peer-comparison-widget">
          <div className="px-4 py-3 border-b border-border">
            <span className="overline-label">Valuation vs {pc.sector} peers ({pc.peer_count} in universe)</span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-4 py-2 overline-label">Metric</th>
                <th className="px-4 py-2 overline-label text-right">{stock.ticker}</th>
                <th className="px-4 py-2 overline-label text-right">Sector avg</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {[
                ["PE", pc.stock?.pe, pc.sector_avg?.pe, false],
                ["PEG", pc.stock?.peg, pc.sector_avg?.peg, false],
                ["Div yield", pc.stock?.dividend_yield, pc.sector_avg?.dividend_yield, true],
              ].map(([label, mine, avg, higherBetter]) => {
                const better = mine != null && avg != null && (higherBetter ? mine > avg : mine < avg);
                return (
                  <tr key={label} className="border-b border-border/50 even:bg-white/[0.02]">
                    <td className="px-4 py-2.5 text-muted-foreground font-body">{label}</td>
                    <td className={`px-4 py-2.5 text-right ${mine != null && avg != null ? (better ? "text-gain" : "text-loss") : "text-white"}`}>
                      {mine != null ? fmtNum(mine) : "—"}
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-300">{avg != null ? fmtNum(avg) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="px-4 py-3">
            <div className="overline-label mb-2">Largest peers</div>
            <div className="flex flex-wrap gap-2">
              {(pc.peers || []).map((p) => (
                <Link key={p.ticker} to={`/stock/${p.ticker}`} data-testid={`peer-chip-${p.ticker}`}
                  className="px-2.5 py-1 border border-border rounded-sm text-xs font-mono text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors duration-150">
                  {p.ticker} · PE {p.pe != null ? fmtNum(p.pe, 1) : "—"}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="widget" data-testid="news-widget">
          <div className="px-4 py-3 border-b border-border">
            <span className="overline-label">Recent headlines</span>
          </div>
          {(stock.news || []).length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">No recent headlines cached for this ticker.</p>
          ) : (
            <ul>
              {stock.news.map((n, i) => (
                <li key={i} className="border-b border-border/50 last:border-0 even:bg-white/[0.02]">
                  <a href={n.url || "#"} target="_blank" rel="noopener noreferrer"
                    className="block px-4 py-3 hover:bg-white/5 transition-colors duration-150 group"
                    data-testid={`news-item-${i}`}>
                    <div className="text-sm text-gray-200 group-hover:text-white leading-snug flex items-start gap-2">
                      <span className="flex-1">{n.title}</span>
                      <ExternalLink className="w-3.5 h-3.5 mt-0.5 shrink-0 text-muted-foreground group-hover:text-primary" />
                    </div>
                    <div className="text-[11px] font-mono text-muted-foreground mt-1">
                      {n.publisher}{n.published_at ? ` · ${new Date(n.published_at).toLocaleDateString()}` : ""}
                    </div>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* AI Research */}
      <AIResearchPanel ticker={stock.ticker} />
    </div>
  );
}
